import { useEffect, useRef } from 'react';

export interface OtpInputProps {
  value: string;
  length?: number;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
}

/** Ввод одноразового кода: N боксов, автопереход, поддержка вставки. */
export function OtpInput({
  value,
  length = 6,
  onChange,
  onComplete,
  disabled = false,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const firstEmpty = value.length < length ? value.length : length - 1;
    refs.current[firstEmpty]?.focus();
  }, [value, length]);

  const setAt = (index: number, char: string): void => {
    const digits = value.split('');
    digits[index] = char;
    const next = digits.join('').slice(0, length);
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  const handleChange = (index: number, raw: string): void => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    setAt(index, digit);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace' && !value[index]) {
      const prev = index - 1;
      if (prev >= 0) {
        const digits = value.split('');
        digits.splice(prev, 1);
        onChange(digits.join(''));
        refs.current[prev]?.focus();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    if (pasted.length === length) onComplete?.(pasted);
  };

  return (
    <div className="flex gap-2.5" role="group" aria-label="Код подтверждения">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          value={value[index] ?? ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          aria-label={`Цифра ${index + 1}`}
          className="h-16 w-12 rounded-2xl border-2 border-accent bg-white/95 text-center text-[28px] font-semibold text-ink outline-none disabled:opacity-60 sm:w-14"
        />
      ))}
    </div>
  );
}
